import { api } from "./api";

/**
 * NECC declared egg rates, and the farm's trade against them.
 *
 * The National Egg Coordination Committee declares a rate per zone each
 * morning, and it is the reference price the Indian layer trade quotes
 * against. This app has always recorded what the farm SOLD at and never what
 * it could have sold at — so a price had nothing to be read against, and a
 * buyer who is quietly 15 paise under on every load was invisible because each
 * individual sale looked fine.
 *
 * See docs/architecture/necc-pricing.md.
 */
export interface NECCRate {
  id: number;
  date: string;
  /** Rupees per EGG. NECC publishes per 100; the form divides. */
  rate_per_egg: number;
  zone: string | null;
  notes: string | null;
}

export interface SaveNECCRate {
  date: string;
  rate_per_egg: number;
  zone: string | null;
  notes: string | null;
}

export interface PriceRealisationDay {
  date: string;
  eggs: number;
  revenue: number;
  necc_rate: number;
  avg_realised: number;
  variance: number;
}

export interface PriceRealisationBuyer {
  buyer_id: number | null;
  buyer_name: string;
  eggs: number;
  revenue: number;
  benchmark: number;
  variance: number;
  avg_realised: number;
  avg_necc: number;
  /** Variance per egg, in paise — the number a farmer argues with a trader in. */
  paise_per_egg: number;
}

export interface PriceRealisation {
  from: string;
  to: string;
  days: PriceRealisationDay[];
  total_eggs: number;
  total_revenue: number;
  benchmark_value: number;
  variance: number;
  avg_realised_per_egg: number;
  avg_necc_per_egg: number;
  unbenchmarked_eggs: number;
  unbenchmarked_days: number;
  by_buyer: PriceRealisationBuyer[];
}

export const neccRateService = {
  list: (params: { from?: string; to?: string; limit?: number } = {}) => {
    const q = new URLSearchParams();
    if (params.from) q.set("from", params.from);
    if (params.to) q.set("to", params.to);
    if (params.limit) q.set("limit", String(params.limit));
    const qs = q.toString();
    return api.get<NECCRate[]>(`/necc-rates${qs ? `?${qs}` : ""}`).then((r) => r.data);
  },

  /**
   * The rate declared on one day, or null.
   *
   * NULL IS NORMAL and is not an error: the server answers 200 with a null
   * body for a day nobody has entered yet, because the sale form asks this on
   * every open and a 404 would make the benchmark look broken every morning
   * before somebody types it in.
   */
  forDate: (date: string) =>
    api
      .get<NECCRate | null>(`/necc-rates/day/${encodeURIComponent(date)}`)
      .then((r) => r.data),

  /** Upsert — NECC revises a rate, and a farm that typed 5.45 for 5.54 must
   *  be able to correct it. */
  save: (data: SaveNECCRate) =>
    api.post<NECCRate>("/necc-rates", data).then((r) => r.data),

  remove: (id: number) => api.delete(`/necc-rates/${id}`).then((r) => r.data),

  realisation: (from?: string, to?: string) => {
    const q = new URLSearchParams();
    if (from) q.set("from", from);
    if (to) q.set("to", to);
    const qs = q.toString();
    return api
      .get<PriceRealisation>(`/necc-rates/realisation${qs ? `?${qs}` : ""}`)
      .then((r) => r.data);
  },
};

/**
 * How a sale price compares with the day's declared rate.
 *
 * Returns null when there is no rate for the day — the caller must render
 * NOTHING rather than a zero comparison, which would read as the farm selling
 * at a 100% discount.
 */
export function compareToNECC(
  pricePerEgg: number,
  rate: NECCRate | null,
): { paise: number; pct: number; over: boolean } | null {
  if (!rate || !Number.isFinite(pricePerEgg) || pricePerEgg <= 0) return null;
  const diff = pricePerEgg - rate.rate_per_egg;
  return {
    // Paise, rounded to two places: the difference lives well below a rupee,
    // and a farm negotiates in paise.
    paise: Math.round(diff * 100 * 100) / 100,
    pct: rate.rate_per_egg > 0 ? Math.round((diff / rate.rate_per_egg) * 1000) / 10 : 0,
    over: diff >= 0,
  };
}

/** NECC publishes per 100 eggs. The entry form takes that figure and this
 *  converts it, so nobody has to do the division in their head — and so the
 *  stored value can never be 100x out. */
export const per100ToPerEgg = (per100: number): number =>
  Math.round((per100 / 100) * 10000) / 10000;
